import type { IdentityVerificationEntry, IdentityVerificationRestrictedAction } from '../types/identityVerification';

const DAY_MS = 24 * 60 * 60 * 1000;

const ALL_ACTIONS: Array<IdentityVerificationRestrictedAction> = [
  'PUBLISH_LISTING',
  'INVITE_TO_APPLY',
  'SETUP_PAYMENTS',
];

export const IDENTITY_VERIFICATION_MOCK_OFF = 'off';

const MOCKS = {
  grace: (now) => ({
    status: 'NOT_STARTED',
    enrollment: { cohort: 'EXISTING', gracePeriodEndsAt: now + 60 * DAY_MS },
    restrictedActions: [],
  }),
  'grace-ending': (now) => ({
    status: 'NOT_STARTED',
    enrollment: { cohort: 'EXISTING', gracePeriodEndsAt: now + 5 * DAY_MS },
    restrictedActions: [],
  }),
  'enforced-new': (now) => ({
    status: 'NOT_STARTED',
    enrollment: { cohort: 'NEW', gracePeriodEndsAt: now - DAY_MS },
    restrictedActions: ALL_ACTIONS,
  }),
  'enforced-existing': (now) => ({
    status: 'NOT_STARTED',
    enrollment: { cohort: 'EXISTING', gracePeriodEndsAt: now - DAY_MS },
    restrictedActions: ['PUBLISH_LISTING'],
  }),
  'in-progress': (now) => ({
    status: 'IN_PROGRESS',
    enrollment: { cohort: 'NEW', gracePeriodEndsAt: now - DAY_MS },
    restrictedActions: ALL_ACTIONS,
  }),
  'needs-review': (now) => ({
    status: 'NEEDS_REVIEW',
    enrollment: { cohort: 'NEW', gracePeriodEndsAt: now - DAY_MS },
    restrictedActions: ALL_ACTIONS,
  }),
  failed: (now) => ({
    status: 'FAILED',
    enrollment: { cohort: 'NEW', gracePeriodEndsAt: now - DAY_MS },
    restrictedActions: ALL_ACTIONS,
  }),
  revoked: (now) => ({
    status: 'REVOKED',
    enrollment: { cohort: 'NEW', gracePeriodEndsAt: now - DAY_MS },
    restrictedActions: ALL_ACTIONS,
  }),
  expired: (now) => ({
    status: 'EXPIRED',
    expiresAt: now - DAY_MS,
    enrollment: { cohort: 'EXISTING', gracePeriodEndsAt: now - 400 * DAY_MS },
    restrictedActions: ['PUBLISH_LISTING'],
  }),
  'renewal-due': (now) => ({
    status: 'VERIFIED',
    expiresAt: now + 20 * DAY_MS,
    enrollment: { cohort: 'EXISTING', gracePeriodEndsAt: now - 345 * DAY_MS },
    restrictedActions: [],
  }),
  verified: (now) => ({
    status: 'VERIFIED',
    expiresAt: now + 300 * DAY_MS,
    enrollment: { cohort: 'EXISTING', gracePeriodEndsAt: now - 65 * DAY_MS },
    restrictedActions: [],
  }),
} satisfies Record<string, (now: number) => Omit<IdentityVerificationEntry, 'category'>>;

export type IdentityVerificationMockState = keyof typeof MOCKS;

export const IDENTITY_VERIFICATION_MOCK_STATES = Object.keys(MOCKS) as Array<IdentityVerificationMockState>;

export const isIdentityVerificationMockState = (value: string | undefined): value is IdentityVerificationMockState =>
  value !== undefined && Object.hasOwn(MOCKS, value);

export const getIdentityVerificationMock = (
  state: IdentityVerificationMockState,
  now: number,
): IdentityVerificationEntry => ({ category: 'DOCUMENT_BIOMETRIC', ...MOCKS[state](now) });
